import { formatListForVerticalityFormSelect } from '@features/siteStatusV2/utils/helpers';
import React, { useEffect, useMemo } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { Control, FormState, UseFormTrigger } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { RootState } from '../../../../app/store';
import { selectAdressInfos } from '../../../eligibility/eligibilitySlice';
import { loadOrders } from '../../reducer/orderListSlice';
import { VerticalityFormInputs } from '../../types';
import AccordionToggle from './AccordionToggle';
import Select from './Select';
import { TranslationNamespaces } from '../../../../locales/constants/translationNamespaces.enum';

interface VerticalityFormProps {
  control: Control<VerticalityFormInputs>;
  formList: {
    buildings: string[];
    stairs: string[];
    floors: string[];
    outlets: string[];
  };
  formValues: VerticalityFormInputs;
  formState: FormState<VerticalityFormInputs>;
  trigger: UseFormTrigger<VerticalityFormInputs>;
}

const VerticalityForm: React.FC<VerticalityFormProps> = ({
  control,
  formValues,
  formList,
  formState,
  trigger
}) => {
  const { t } = useTranslation(TranslationNamespaces.SITE_STATUS);
  const dispatch = useAppDispatch();
  const addressInfos = useAppSelector(selectAdressInfos);
  const { site } = useSelector((state: RootState) => state.site);
  const { buildings, stairs, floors, outlets } = formList;
  const selectAll = t('OrderList.all');

  const formatVerticalitySelectList = useMemo(() => {
    if (!site?.buildings?.length) {
      return {
        buildingsList: [],
        stairsList: [],
        floorsList: [],
        outletsList: []
      };
    }

    const formatList = (items: string[]) => formatListForVerticalityFormSelect(items);

    return {
      buildingsList: formatList(buildings),
      stairsList: formatList(stairs),
      floorsList: formatList(floors),
      outletsList: formatList(outlets)
    };
  }, [site, buildings, stairs, floors, outlets]);

  const { buildingsList, stairsList, floorsList, outletsList } = formatVerticalitySelectList;

  const getOrders = () => {
    if (!addressInfos?.buildingRef) return;

    const { building, stair, floor, outlet } = formValues;

    const filterSelection = (value: string | undefined, list: string[]) =>
      value && value !== selectAll ? [value] : list;

    dispatch(
      loadOrders({
        buildingId: addressInfos.buildingRef,
        building: filterSelection(building, buildings),
        stair: filterSelection(stair, stairs),
        floor: filterSelection(floor, floors),
        outletReference: filterSelection(outlet, outlets),
        hexacleAddress: site?.hexacleAddress ?? ''
      })
    );
  };

  useEffect(() => {
    if (site?.buildings?.length) {
      trigger('building');
    }
  }, [formList]);

  return (
    <Form autoComplete="off">
      <Row className="mt-3 mb-2">
        <Col className="col-md-3">
          <Select
            options={buildingsList}
            selectdValue={formValues.building}
            label={t('verticalityForm.building', `Building`)}
            placeholder={t('verticalityForm.building', `Building`)}
            registerTitle="building"
            control={control}
            rules={{ required: true }}
          />
        </Col>
        <Col className="col-md-3">
          <Select
            options={stairsList}
            selectdValue={formValues.stair}
            label={t('verticalityForm.stair', `Stair`)}
            placeholder={t('verticalityForm.stair', `Stair`)}
            registerTitle="stair"
            control={control}
            rules={{ required: true }}
          />
        </Col>
        <Col className="col-md-3">
          <Select
            options={floorsList}
            selectdValue={formValues.floor}
            label={t('verticalityForm.level', `Level`)}
            placeholder={t('verticalityForm.level', `Level`)}
            registerTitle="floor"
            control={control}
            rules={{ required: true }}
          />
        </Col>
        <Col className="col-md-3">
          <Select
            options={outletsList}
            selectdValue={formValues.outlet}
            label={t('verticalityForm.ptoList', `PTO list`)}
            placeholder={t('verticalityForm.ptoList', `PTO list`)}
            registerTitle="outlet"
            control={control}
            rules={{ required: true }}
          />
        </Col>
        <Col className="col-md-12 d-flex justify-content-end mt-4">
          <AccordionToggle eventKey="0" disabled={!formState.isValid} onClick={getOrders}>
            {t('verticalityForm.see', `See`)}
          </AccordionToggle>
        </Col>
      </Row>
    </Form>
  );
};

export default VerticalityForm;
